import React, { useEffect, useState, useMemo } from "react";
import { NavLink, useLocation } from "react-router-dom";
import {
  House, FileText, PenNib, Gavel, ChartBar, ShieldCheck, Link as LinkIcon,
  Buildings, ChartLineUp, GearSix, SignOut, Sparkle, UsersThree, CreditCard, Lock, ShieldCheckered,
  Crown, PaintBrush, FileDoc, Package,
} from "@phosphor-icons/react";
import { useAuth } from "@/contexts/AuthContext";
import { useI18n } from "@/contexts/I18nContext";
import { useRBAC } from "@/contexts/RBACContext";
import { motion } from "framer-motion";
import api from "@/lib/api";
import Brand from "@/components/Brand";

export function SidebarContent({ onOpenPalette, onNavigate }) {
  const { user, logout } = useAuth();
  const { t } = useI18n();
  const { allowed, ready: rbacReady } = useRBAC();
  const [branding, setBranding] = useState(null);

  useEffect(() => {
    if (!user?.impersonating && !user) return;
    api.get("/tenant/branding").then(r => setBranding(r.data || {})).catch(() => {});
  }, [user?.tenant_id]);

  const logoUrl = branding?.logo?.url;

  const GROUPS_RAW = [
    {
      label: "Operate",
      items: [
        { to: "/app", label: t("nav.dashboard", "Dashboard"), icon: House, end: true, testid: "dashboard", module: "dashboard" },
        { to: "/app/studio", label: t("nav.studio", "Contract Studio"), icon: PenNib, testid: "studio", module: "studio" },
        { to: "/app/repository", label: t("nav.repository", "Repository"), icon: FileText, testid: "repository", module: "repository" },
        { to: "/app/workflows", label: t("nav.workflows", "Workflows"), icon: Gavel, testid: "workflows", module: "workflows" },
      ],
    },
    {
      label: "Intelligence",
      items: [
        { to: "/app/impact", label: t("nav.impact", "Business Impact"), icon: ChartBar, testid: "impact", module: "bir" },
        { to: "/app/monitoring", label: t("nav.monitoring", "Monitoring"), icon: ShieldCheck, testid: "monitoring", module: "monitoring" },
        { to: "/app/counterparty", label: t("nav.counterparty", "Counterparty 360°"), icon: Buildings, testid: "counterparty", module: "counterparty" },
        { to: "/app/analytics", label: t("nav.analytics", "Analytics"), icon: ChartLineUp, testid: "analytics", module: "analytics" },
      ],
    },
    {
      label: "Admin",
      items: [
        { to: "/app/team", label: t("nav.team", "Team & Roles"), icon: UsersThree, testid: "team", module: "team" },
        { to: "/app/roles-permissions", label: "Roles & Permissions", icon: ShieldCheckered, testid: "roles-permissions", module: "roles_permissions" },
        { to: "/app/boilerplates", label: "Boilerplates", icon: FileDoc, testid: "boilerplates", module: "boilerplates" },
        { to: "/app/doc-types", label: "Doc Types → Workflows", icon: Gavel, testid: "doc-types", module: "doc_types" },
        { to: "/app/starter-packs", label: "Starter Packs", icon: Package, testid: "starter-packs", module: "starter_packs" },
        { to: "/app/branding", label: "Branding", icon: PaintBrush, testid: "branding", module: "branding" },
        { to: "/app/integrations", label: t("nav.integrations", "Integrations"), icon: LinkIcon, testid: "integrations", module: "integrations" },
        { to: "/app/compliance", label: t("nav.compliance", "Compliance"), icon: ShieldCheckered, testid: "compliance", module: "compliance" },
        { to: "/app/sso", label: "SSO & Directory", icon: Lock, testid: "sso", module: "integrations" },
        { to: "/app/subscription", label: t("nav.subscription", "Subscription"), icon: CreditCard, testid: "subscription", module: "subscription" },
        { to: "/app/settings", label: t("nav.settings", "Settings"), icon: GearSix, testid: "settings", module: "team" },
      ],
    },
  ];

  // Filter by RBAC allowed modules (global staff bypass via allowed being full set)
  // and append the staff-only "Clariva HQ" group. Return a fresh array each time.
  const GROUPS = useMemo(() => {
    const base = !rbacReady || !allowed.size
      ? GROUPS_RAW
      : GROUPS_RAW.map(g => ({
          ...g,
          items: g.items.filter(it => !it.module || allowed.has(it.module)),
        })).filter(g => g.items.length > 0);
    const isStaff = user?.is_global || ["global_admin", "clariva_support"].includes(user?.role);
    if (!isStaff) return base;
    return [
      ...base,
      {
        label: "Clariva HQ",
        items: [
          { to: "/app/global-admin", label: "Global Admin", icon: Crown, testid: "global-admin", module: "global_admin" },
        ],
      },
    ];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allowed, rbacReady, t, user?.is_global, user?.role]);

  return (
    <div className="h-full flex flex-col">
      <div className="px-6 pt-8 pb-4 flex items-center gap-3" data-testid="sidebar-brand">
        {logoUrl ? (
          <img src={logoUrl} alt="Tenant logo" className="w-9 h-9 rounded-md object-contain bg-[var(--surface)] border border-[var(--border)]" data-testid="sidebar-tenant-logo" />
        ) : (
          <Brand size={32} testId="sidebar-brand-mark" />
        )}
        <div>
          <div className="text-lg font-semibold tracking-tight" style={{ fontFamily: 'var(--font-heading)' }}>Clariva</div>
          <div className="overline text-[10px]">{branding?.footer_text ? "Powered by Clariva" : "Contract Intelligence"}</div>
        </div>
      </div>

      <button
        onClick={onOpenPalette}
        data-testid="open-command-palette-btn"
        className="mx-4 mb-3 text-left px-3 py-2 rounded-md glass-subtle text-sm text-[var(--text-2)] hover:text-[var(--text)] flex items-center justify-between"
      >
        <span>{t("common.ask", "Ask Clariva…")}</span>
        <kbd className="font-mono text-xs px-1.5 py-0.5 rounded bg-[var(--surface-strong)] border border-[var(--border)]">⌘K</kbd>
      </button>

      <nav className="flex-1 overflow-y-auto px-3 pb-4 space-y-1">
        {GROUPS.map((g) => (
          <div key={g.label} className="mb-3">
            <div className="overline px-3 mb-1 text-[10px]">{g.label}</div>
            <div className="space-y-0.5">
              {g.items.map((item) => (
                <NavLink
                  key={item.to}
                  to={item.to}
                  end={item.end}
                  onClick={onNavigate}
                  data-testid={`nav-${item.testid}`}
                  className={({ isActive }) =>
                    `flex items-center gap-3 px-3 py-2 rounded-md text-sm transition-all ${
                      isActive
                        ? "bg-[var(--surface-strong)] text-[var(--text)] border border-[var(--border-strong)]"
                        : "text-[var(--text-2)] hover:text-[var(--text)] hover:bg-[var(--surface-2)]"
                    }`
                  }
                >
                  {({ isActive }) => (
                    <>
                      <item.icon size={16} weight={isActive ? "duotone" : "regular"} />
                      <span className="truncate">{item.label}</span>
                      {isActive && <motion.div layoutId="nav-dot" className="ms-auto w-1.5 h-1.5 rounded-full bg-[var(--accent)]" />}
                    </>
                  )}
                </NavLink>
              ))}
            </div>
          </div>
        ))}
      </nav>

      <div className="p-3 border-t border-[var(--border)]">
        <div className="flex items-center gap-3 px-2 py-2">
          <div className="w-9 h-9 rounded-full bg-[var(--surface-strong)] border border-[var(--border-strong)] flex items-center justify-center font-semibold text-sm">
            {user?.full_name?.charAt(0) || "U"}
          </div>
          <div className="min-w-0 flex-1">
            <div className="text-sm font-medium truncate">{user?.full_name}</div>
            <div className="text-xs text-[var(--text-3)] truncate">{user?.email}</div>
          </div>
          <button onClick={logout} data-testid="logout-btn" className="text-[var(--text-3)] hover:text-[var(--danger)] transition p-1" aria-label="Logout">
            <SignOut size={18} />
          </button>
        </div>
      </div>
    </div>
  );
}

export default function Sidebar(props) {
  return (
    <aside className="hidden lg:flex h-screen sticky top-0 w-64 flex-col border-e border-[var(--border)] bg-[var(--bg-2)]/60 backdrop-blur-2xl">
      <SidebarContent {...props} />
    </aside>
  );
}
